import {useCallback, useEffect, useMemo, useState} from 'react';

import {ProteinCreationSettingsData} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import ProteinCreationStructuredTableAdapter, {
  ProteinCreationStructuredTable,
  ProteinCreationTableMetadata,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationStructuredTableAdapter';
import {
  createBlankRow,
  parseStructuredTableToRows,
  proteinCreationRowsToStructuredTable,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import {ProteinCreationRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  ProteinSchemaLinkedSchemas,
  queryProteinSchemaLinkedSchemas,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {AntibodyComponentAndRelatedSchemas} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {
  SchemaAndColumnGroup,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';

const TABLE_CID = 'protein-creation-table';
const INITIAL_ROW_COUNT = 1;

export interface ProteinCreationTableState {
  rows: Array<ProteinCreationRow>;
  structuredTable: ProteinCreationStructuredTable;
  handleTableUpdate: (newTable: UnknownStructuredTable) => void;
  /** Replace every row in the table with the given rows. */
  replaceRows: (newRows: Array<ProteinCreationRow>) => void;
  // The protein schema supplies the table name and schema field columns.
  isSchemaLoaded: boolean;
}

/**
 * `componentAndRelatedSchemas` is fetched by the caller (the protein creation flow machine, which also
 * owns error handling for that fetch); the table is unavailable until it is non-null.
 */
export default function useProteinCreationTableState(
  creationSettings: ProteinCreationSettingsData,
  componentAndRelatedSchemas: AntibodyComponentAndRelatedSchemas | null
): ProteinCreationTableState | null {
  const structuredTableInsertionContext = StructuredTableInsertionContext.useContext();
  const {tagSchema} = useTagSchemaQuery(creationSettings.selectedSchemaId);

  const schemas: Array<SchemaAndColumnGroup> = useMemo(
    () => (tagSchema ? [{...graphQLToREST('TagSchema', tagSchema), columnGroup: null}] : []),
    [tagSchema]
  );

  const [linkedSchemas, setLinkedSchemas] = useState<ProteinSchemaLinkedSchemas | null>(null);
  useEffect(() => {
    async function fetchLinkedSchemas(): Promise<void> {
      setLinkedSchemas(await queryProteinSchemaLinkedSchemas(creationSettings.selectedSchemaId));
    }
    fetchLinkedSchemas();
  }, [creationSettings.selectedSchemaId]);

  const tableName = tagSchema != null ? `${tagSchema.name} protein creation table` : '';

  const aaSchemaIdByComponentLabel = useMemo(() => {
    if (linkedSchemas == null) {
      return new Map<string, string>();
    }
    const {domains} = creationSettings.selectedFormat;
    const map = new Map<string, string>();
    for (const {domainType, specificity, schemaOrInterfaceId} of linkedSchemas.domainLinkedSchemas) {
      if (schemaOrInterfaceId != null) {
        const domain = domains.find((d) => d.type === domainType && d.specificity === specificity);
        if (domain != null) {
          map.set(domain.label, schemaOrInterfaceId);
        }
      }
    }
    for (const {label, schemaOrInterfaceId} of linkedSchemas.chainLinkedSchemas) {
      if (schemaOrInterfaceId != null) {
        map.set(label, schemaOrInterfaceId);
      }
    }
    return map;
  }, [creationSettings.selectedFormat, linkedSchemas]);

  const baseTable = useMemo<ProteinCreationStructuredTable | null>(() => {
    if (componentAndRelatedSchemas == null) {
      return null;
    }
    const adapter = ProteinCreationStructuredTableAdapter.getAdapter();
    const metadata: ProteinCreationTableMetadata = {
      antibodyFormat: creationSettings.selectedFormat,
      startingType: creationSettings.selectedStartingType,
      componentAndRelatedSchemas,
      aaSchemaIdByComponentLabel,
    };
    const created = adapter.createTable({
      name: tableName,
      metadata,
      entryId: null,
      schemas,
      selectorsById: {},
      structuredTableInsertionContext,
    });
    created.provenanceUuid = TABLE_CID;
    return {...created, cid: TABLE_CID};
  }, [
    aaSchemaIdByComponentLabel,
    componentAndRelatedSchemas,
    creationSettings.selectedFormat,
    creationSettings.selectedStartingType,
    schemas,
    structuredTableInsertionContext,
    tableName,
  ]);

  const [rows, setRows] = useState<Array<ProteinCreationRow> | null>(null);

  useEffect(() => {
    if (baseTable != null && rows == null) {
      setRows(Array.from({length: INITIAL_ROW_COUNT}, () => createBlankRow(baseTable.columns)));
    }
  }, [baseTable, rows]);

  const structuredTable = useMemo(
    () => (baseTable && rows ? proteinCreationRowsToStructuredTable(baseTable, rows) : null),
    [baseTable, rows]
  );

  const handleTableUpdate = useCallback(
    (newTable: UnknownStructuredTable) => {
      if (baseTable == null) {
        return;
      }
      setRows((prevRows) =>
        parseStructuredTableToRows(
          newTable.rows as ProteinCreationStructuredTable['rows'],
          baseTable.columns,
          prevRows ?? []
        )
      );
    },
    [baseTable]
  );

  if (structuredTable == null) {
    return null;
  }

  return {
    rows: rows!,
    structuredTable,
    handleTableUpdate,
    replaceRows: setRows,
    isSchemaLoaded: tagSchema != null,
  };
}
